import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ApolloCache, PureQueryOptions } from '@apollo/client';

import { GlobalToastTypeNames, ToastTypes } from 'types';
import {
  useStudentConversationsPagedQuery,
  useGroupConversationsPagedQuery,
  useTeacherConversationsPagedQuery,
  usePostSlackReplyMutation,
  usePostGmailReplyMutation,
  useMarkConvReadMutation,
  useSetConvStatusMutation,
  StudentConversationsPagedDocument,
  GroupConversationsPagedDocument,
  TeacherConversationsPagedDocument,
  SetConvStatusMutation,
} from 'core/graphql';

import { useToasts } from '../Toast/useToasts';
import { IRichEmailPayload } from '../RichMail/useRichEmailDraft';
import { richMailSendErrorKey } from '../RichMail/richMailModel';
import {
  appendThreadMessage,
  ICascadeTriagePayload,
  patchCascadeTriage,
  patchThreadTriage,
} from './threadCache';
import { commsCounterRefetch, replyTriageRefetch } from './commsCounterQueries';

export type ConversationStatus = 'open' | 'in_progress' | 'done';

export interface IConversation {
  id: string;
  kind: string;
  title: string;
  /** Human dialog title (channel name / mail subject / counterpart name). */
  displayTitle: string;
  externalId: string;
  /** Slack workspace team id — powers the «open in Slack» deep link. */
  teamId: string;
  mailboxEmail: string | null;
  unreadCount: number;
  status: string;
  hasUnhandled: boolean;
  lastMessageAt: string | null;
}

/** Узел как он приходит по проводу: у codegen каждое поле nullable. */
type IWireConversation = {
  [K in keyof IConversation]?: IConversation[K] | null;
};

interface IConversationConnection {
  totalCount: number;
  totalCountCapped?: boolean | null;
  pageInfo?: {
    hasNextPage?: boolean | null;
    endCursor?: string | null;
  } | null;
  edges?: Array<{ node?: IWireConversation | null } | null> | null;
}

interface IArgs {
  studentId?: string;
  groupId?: string;
  teacherId?: string;
  /**
   * Оставить только эти типы диалогов — фильтр уходит НА СЕРВЕР (ТЗ 0031).
   * Пока панель Slack фильтровала у себя, она скачивала ещё и почтовые треды
   * того же студента, чтобы тут же их выбросить.
   */
  kinds?: string[];
}

interface IUseConversations {
  conversations: IConversation[];
  /** Запрос упал. Пустой список при этом НЕ значит «переписки нет». */
  error: boolean;
  /** Всего диалогов под фильтром (может быть капнуто, см. totalCapped). */
  total: number;
  /** true → диалогов больше, чем `total`; счётчик остановился на потолке. */
  totalCapped: boolean;
  /** Есть ли следующая страница. */
  hasMore: boolean;
  /** Догрузить следующую страницу (мержится relayStylePagination). */
  loadMore: () => void;
  loading: boolean;
  replying: boolean;
  reply: (
    conversationId: string,
    text: string,
    kind: string,
    /** ТЗ 0097: ответ по почте — HTML и вложения вместо `text`. */
    rich?: IRichEmailPayload,
    /** Перепроверка F35: самое новое показанное входящее (`newestInboundId`). */
    seenMessageId?: string | null,
  ) => Promise<boolean>;
  /** Mark every message of a conversation read/unread (team-wide) + refetch. */
  markRead: (conversationId: string, read?: boolean) => Promise<void>;
  /** Set the handling status (open / in_progress / done) + refetch. */
  setStatus: (
    conversationId: string,
    status: ConversationStatus,
  ) => Promise<void>;
  /**
   * Refetch options for the conversation list — hand these to a sibling
   * mutation (e.g. create-task-from-message) so the list refreshes too.
   */
  listRefetch: PureQueryOptions | null;
}

const GMAIL_KIND = 'gmail_thread';

/** Размер страницы списка диалогов (ТЗ 0031, решение владельца). */
const CONVERSATION_PAGE = 10;

/** Страница треда. Обязана совпадать с `THREAD_PAGE` в CommunicationsPanel:
 *  у `conversationThread` в кэше keyArgs — только `conversationId`, так что
 *  любая другая величина пишется в ТОТ ЖЕ список. */
export const CONVERSATION_THREAD_PAGE = 15;

type ErrList =
  | Array<{
      field?: string | null;
      message?: string | null;
      code?: string | null;
    } | null>
  | null
  | undefined;

const errText = (errors: ErrList): string | undefined =>
  errors
    ?.map((e) => e?.message || e?.code || null)
    .filter(Boolean)
    .join('\n') || undefined;

/**
 * Перепроверка G5: ключ перевода для отказа ответа, у которого есть слова
 * ТЗ 0097 (HTML_TOO_LARGE / HTML_TOO_DEEP / вложения) — как у ThreadView,
 * BroadcastDrawer и `sendNewEmail`. Сервер пишет `message` по-русски, и в
 * польском интерфейсе тост выходил русским. Нет ключа — `null`.
 */
const replyErrorKey = (errors: ErrList): string | null => {
  const known = (errors || []).find(
    (error) => error && richMailSendErrorKey(error),
  );
  return known ? richMailSendErrorKey(known) : null;
};

/**
 * Slack/Gmail conversations for a student, a group OR a teacher + reply
 * (spec 0003 Phase 3; teacher branch added in spec 0007).
 *
 * Triage mutations (markRead / setStatus) are cache-first (spec 0008): the
 * mutation payload carries the changed conversation, Apollo merges it into
 * the normalized entity, and every list/badge that selects those fields
 * re-renders from the cache — nothing blocks on a refetch. The 60s badge
 * poll remains the cross-client convergence safety net.
 */
export const useConversations = ({
  studentId,
  groupId,
  teacherId,
  kinds,
}: IArgs): IUseConversations => {
  const { t } = useTranslation();
  const { addToast } = useToasts();

  // Переменные ОДНИ на все три ветки: они же уходят в `listRefetch`, и
  // разъехавшийся набор дал бы промах по кэшу — refetch создал бы вторую
  // запись вместо обновления показанной.
  const paging = useMemo(() => ({ kinds, first: CONVERSATION_PAGE }), [kinds]);

  // errorPolicy:'all' so a PermissionDenied (e.g. a non-management role opening
  // a card) degrades to an empty panel instead of an unhandled query error.
  const studentQuery = useStudentConversationsPagedQuery({
    variables: { studentId: studentId || '', ...paging },
    skip: !studentId,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });
  const groupQuery = useGroupConversationsPagedQuery({
    variables: { groupId: groupId || '', ...paging },
    skip: !groupId,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });
  const teacherQuery = useTeacherConversationsPagedQuery({
    variables: { teacherId: teacherId || '', ...paging },
    skip: !teacherId,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });

  const listRefetch = useMemo(() => {
    if (studentId) {
      return {
        query: StudentConversationsPagedDocument,
        variables: { studentId, ...paging },
      };
    }
    if (groupId) {
      return {
        query: GroupConversationsPagedDocument,
        variables: { groupId, ...paging },
      };
    }
    if (teacherId) {
      return {
        query: TeacherConversationsPagedDocument,
        variables: { teacherId, ...paging },
      };
    }
    return null;
  }, [studentId, groupId, teacherId, paging]);

  const [slackMut, slackState] = usePostSlackReplyMutation();
  const [gmailMut, gmailState] = usePostGmailReplyMutation();
  const [markConvRead] = useMarkConvReadMutation();
  const [setConvStatus] = useSetConvStatusMutation();

  const markRead = useCallback(
    async (conversationId: string, read = true) => {
      try {
        await markConvRead({
          variables: { conversationId, read },
          // The payload's conversation { unreadCount, hasUnhandled } merges
          // into the entity and updates every list and badge built on it.
          // Only the rail / menu counters (rows without an id) need a
          // refetch (ТЗ 0096; menu counters by document, role-gated — F47). The update() flips the open thread's per-message
          // read flags in the cache so unread styling clears too — via the
          // helper shared with useBulkTriage's conversation scope, so the two
          // paths cannot patch the same thread differently (spec 0030).
          refetchQueries: commsCounterRefetch(),
          update: (cache) => patchThreadTriage(cache, conversationId, { read }),
        });
      } catch {
        // Non-critical: a failed read-marker shouldn't disrupt reading.
      }
    },
    [markConvRead],
  );

  const setStatus = useCallback(
    async (conversationId: string, status: ConversationStatus) => {
      try {
        await setConvStatus({
          variables: { conversationId, status },
          // The status paints instantly; Apollo rolls the write back
          // automatically if the mutation errors.
          //
          // No queue surgery here any more (round 3): `done` USED to drop a
          // dialog from the server's «ждут ответа» population, so the cache
          // mirrored that locally. The server now ignores status entirely —
          // evicting here would make the row vanish and then reappear on the
          // next fetch. Leaving the queue is the tick's job (useMessageTriage).
          optimisticResponse: {
            setConversationStatus: {
              __typename: 'SetConversationStatusMutation',
              success: true,
              customErrors: null,
              conversation: {
                __typename: 'ConversationType',
                id: conversationId,
                status,
              },
            },
          } as SetConvStatusMutation,
        });
      } catch {
        // Non-critical.
      }
    },
    [setConvStatus],
  );

  const reply = useCallback(
    async (
      conversationId: string,
      text: string,
      kind: string,
      /** ТЗ 0097: ответ по почте — HTML редактора и вложения. */
      rich?: IRichEmailPayload,
      /**
       * Перепроверка F35: самое новое входящее, показанное в треде в момент
       * «Отправить» (`newestInboundId`). `null` — входящих на экране нет.
       */
      seenMessageId?: string | null,
    ) => {
      const isGmail = kind === GMAIL_KIND;
      // Ответ = прочитано и обработано входящее, которое менеджер видел
      // (`seenMessageId`; решение владельца; сервер делает это сам после
      // успешной отправки и отдаёт тот же отчёт, что каскадный тик). Обе
      // мутации делят один `opts`, поэтому разбор ответа общий: `unknown` +
      // сужение внутри.
      const triageOf = (res: { data?: unknown }) => {
        const data = (res.data || {}) as Record<
          string,
          | ({
              message?: { id?: string } | null;
            } & ICascadeTriagePayload)
          | null
        >;
        return data.sendSlackMessage || data.sendGmailReply || null;
      };
      const opts = {
        // ТЗ 0033: тред НЕ рефетчим. Рефетч первой страницы затирал всё, что
        // менеджер догрузил кнопкой «показать более ранние» (merge Apollo на
        // странице без `after` обнуляет префикс), а список диалогов
        // откатывался на первые десять. Сервер вернул созданное сообщение —
        // дописываем его в кэш сами.
        //
        // Список диалогов рефетчим по-прежнему: у его строк меняется
        // `hasUnhandled`/`lastMessageAt`, и это не патчится из payload'а
        // ответа. Он же и восстановит страницы, если их успели догрузить —
        // цена меньше, чем потерянный тред.
        //
        // Если ответ что-то погасил — перечитываем всё, что перечитывает
        // массовая обработка (очередь, лента группы, рейка, меню, списки
        // диалогов — по ИМЕНИ операции, т.е. и наш список тоже) плюс значки
        // студентов группы. Ничего не погасил (ответ в уже разобранный
        // диалог) — только свой список, как раньше.
        refetchQueries: (res: { data?: unknown }) => {
          const out = triageOf(res);
          const flipped =
            !!out?.truncated ||
            (out?.readMessageIds?.length || 0) > 0 ||
            (out?.handledMessageIds?.length || 0) > 0;
          if (flipped) return replyTriageRefetch();
          return listRefetch ? [listRefetch] : [];
        },
        update: (cache: ApolloCache<unknown>, res: { data?: unknown }) => {
          const out = triageOf(res);
          const msg = out?.message;
          if (msg?.id) {
            appendThreadMessage(
              cache,
              conversationId,
              msg as { id: string } & Record<string, unknown>,
            );
          }
          // Пузыри открытого треда и ленты группы. Списки неполные — красим
          // тред целиком: всё, что в нём было до ответа, сервер закрыл.
          if (out && !patchCascadeTriage(cache, out) && out.truncated) {
            patchThreadTriage(cache, conversationId, {
              read: true,
              handled: true,
            });
          }
        },
      };
      try {
        const p = isGmail
          ? (
              await gmailMut({
                ...opts,
                variables: rich
                  ? {
                      conversationId,
                      html: rich.html,
                      attachmentIds: rich.attachmentIds,
                      seenMessageId: seenMessageId || null,
                    }
                  : {
                      conversationId,
                      text,
                      seenMessageId: seenMessageId || null,
                    },
              })
            ).data?.sendGmailReply
          : (
              await slackMut({
                ...opts,
                variables: {
                  conversationId,
                  text,
                  seenMessageId: seenMessageId || null,
                },
              })
            ).data?.sendSlackMessage;
        if (p?.success) return true;
        const errorKey = replyErrorKey(p?.customErrors);
        addToast({
          data: {
            type: ToastTypes.error,
            title: t('pages.communications.failed'),
            text: errorKey ? t(errorKey) : errText(p?.customErrors),
          },
          type: GlobalToastTypeNames.FakturowniaMatch,
        });
        return false;
      } catch (e) {
        addToast({
          data: {
            type: ToastTypes.error,
            title: t('pages.communications.failed'),
            text: (e as Error).message,
          },
          type: GlobalToastTypeNames.FakturowniaMatch,
        });
        return false;
      }
    },
    [slackMut, gmailMut, listRefetch, addToast, t],
  );

  // Три операции возвращают ОДИН тип `ConversationConnection`, но codegen
  // печатает его отдельно под каждую — отсюда явная форма вместо вывода.
  let connection: IConversationConnection | undefined;
  if (studentId) {
    connection = studentQuery.data?.studentConversationsPage;
  } else if (teacherId) {
    connection = teacherQuery.data?.teacherConversationsPage;
  } else {
    connection = groupQuery.data?.groupConversationsPage;
  }
  const rawConversations = useMemo(
    () =>
      (connection?.edges || [])
        .map((edge) => edge?.node)
        .filter((node): node is NonNullable<typeof node> => Boolean(node)),
    [connection],
  ) as IConversation[];
  const hasMore = Boolean(connection?.pageInfo?.hasNextPage);
  const endCursor = connection?.pageInfo?.endCursor || null;
  // Три `fetchMore` вместо одного приведённого типами queryResult: у трёх
  // операций разные типы переменных, и общий каст пришлось бы гнать через
  // `unknown`, потеряв проверку аргументов там, где она как раз и нужна.
  const { fetchMore: fetchMoreStudent } = studentQuery;
  const { fetchMore: fetchMoreGroup } = groupQuery;
  const { fetchMore: fetchMoreTeacher } = teacherQuery;
  const loadMore = useCallback(() => {
    // `after: null` вернул бы ПЕРВУЮ страницу, и relay-merge склеил бы её
    // саму с собой — список замер бы, а кнопка осталась гореть.
    if (!endCursor) return;
    if (studentId) fetchMoreStudent({ variables: { after: endCursor } });
    else if (teacherId) fetchMoreTeacher({ variables: { after: endCursor } });
    else fetchMoreGroup({ variables: { after: endCursor } });
  }, [
    endCursor,
    studentId,
    teacherId,
    fetchMoreStudent,
    fetchMoreGroup,
    fetchMoreTeacher,
  ]);

  // Backend resolves displayTitle for every kind; the || chain only guards
  // against a stale cache entry fetched before the field existed.
  const conversations = useMemo(
    () =>
      (rawConversations || []).map((c) => ({
        ...c,
        displayTitle: c.displayTitle || c.title || c.externalId,
        teamId: c.teamId || '',
      })),
    [rawConversations],
  );

  let listLoading = groupQuery.loading;
  let listError = groupQuery.error;
  if (studentId) {
    listLoading = studentQuery.loading;
    listError = studentQuery.error;
  } else if (teacherId) {
    listLoading = teacherQuery.loading;
    listError = teacherQuery.error;
  }

  return {
    conversations,
    // ТЗ 0033: у трёх запросов `errorPolicy: 'all'`, и без этого поля упавший
    // запрос выглядел как «переписки нет» — панель рисовала «Начать
    // переписку», и менеджер отправлял «первое» сообщение в существующий
    // тред. Эталон рядом: `useConversationsBrowser`.
    error: Boolean(listError),
    total: connection?.totalCount ?? conversations.length,
    totalCapped: Boolean(connection?.totalCountCapped),
    hasMore,
    loadMore,
    loading: listLoading,
    replying: slackState.loading || gmailState.loading,
    reply,
    markRead,
    setStatus,
    listRefetch,
  };
};
